import { useState } from 'react';
import { motion } from 'framer-motion';
import { Newspaper, ExternalLink } from 'lucide-react';
import { ConfidenceBar } from '@/components/profiler/ConfidenceBar';
import { ADVERSE_COLOR } from '@/types/profiler';
import type { AdverseCategory } from '@/types/profiler';
import type { DossierSectionProps } from './types';

// ── filter pill ───────────────────────────────────────────────────────────────

const FILTER_LABELS: { key: AdverseCategory | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'ALL' },
  { key: 'CRIMINAL', label: 'CRIMINAL' },
  { key: 'FRAUD', label: 'FRAUD' },
  { key: 'REGULATORY_ACTION', label: 'REGULATORY' },
  { key: 'INSOLVENCY', label: 'INSOLVENCY' },
  { key: 'ADVERSE_OTHER', label: 'OTHER' },
];

function FilterPill({
  label,
  active,
  color,
  count,
  onClick,
}: {
  label: string;
  active: boolean;
  color: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? `${color}20` : '#0C1118',
        color: active ? color : '#5A7A9A',
        border: `1px solid ${active ? color + '50' : '#182030'}`,
        fontSize: 9,
        fontFamily: 'monospace',
        fontWeight: active ? 700 : 400,
        padding: '4px 10px',
        borderRadius: 99,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {label} {count > 0 && <span style={{ opacity: 0.7 }}>({count})</span>}
    </button>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function categoryLabel(cat: AdverseCategory): string {
  return cat.replace('_', ' ');
}

// ── main component ─────────────────────────────────────────────────────────────

export function AdverseMedia({ pipeline }: DossierSectionProps) {
  const [activeFilter, setActiveFilter] = useState<AdverseCategory | 'ALL'>('ALL');

  const adverse = pipeline?.adverse_media;
  const articles = adverse?.articles ?? [];

  const adverseArticles = articles.filter(a => a.category !== 'NOT_ADVERSE');
  const highestConfidence = adverseArticles.reduce((max, a) => Math.max(max, a.confidence), 0);
  const avgConfidence = adverseArticles.length > 0
    ? adverseArticles.reduce((sum, a) => sum + a.confidence, 0) / adverseArticles.length
    : 0;

  // Category distribution
  const catCounts: Partial<Record<AdverseCategory, number>> = {};
  for (const a of adverseArticles) {
    catCounts[a.category] = (catCounts[a.category] ?? 0) + 1;
  }

  const filtered = activeFilter === 'ALL'
    ? adverseArticles
    : adverseArticles.filter(a => a.category === activeFilter);

  // Determine highest severity label
  const severityOrder: AdverseCategory[] = ['CRIMINAL', 'FRAUD', 'REGULATORY_ACTION', 'INSOLVENCY', 'ADVERSE_OTHER', 'NOT_ADVERSE'];
  let highestSeverity: AdverseCategory = 'NOT_ADVERSE';
  for (const cat of severityOrder) {
    if ((catCounts[cat] ?? 0) > 0) {
      highestSeverity = cat;
      break;
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5"
    >
      {/* Summary metrics */}
      <div className="grid grid-cols-4 gap-3">
        <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 800, fontSize: 22 }}>
            {articles.length}
          </div>
          <div style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 4 }}>TOTAL ARTICLES</div>
        </div>
        <div style={{ background: '#0C1118', border: `1px solid ${adverseArticles.length > 0 ? '#FF6B1A30' : '#182030'}`, borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ color: adverseArticles.length > 0 ? '#FF6B1A' : '#5A7A9A', fontFamily: 'monospace', fontWeight: 800, fontSize: 22 }}>
            {adverseArticles.length}
          </div>
          <div style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 4 }}>ADVERSE</div>
        </div>
        <div style={{ background: '#0C1118', border: `1px solid ${ADVERSE_COLOR[highestSeverity]}20`, borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ color: ADVERSE_COLOR[highestSeverity], fontFamily: 'monospace', fontWeight: 800, fontSize: 12, lineHeight: 1.4 }}>
            {adverseArticles.length > 0 ? categoryLabel(highestSeverity) : 'NONE'}
          </div>
          <div style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 4 }}>HIGHEST SEVERITY</div>
        </div>
        <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ color: '#00C8FF', fontFamily: 'monospace', fontWeight: 800, fontSize: 22 }}>
            {Math.round(avgConfidence * 100)}%
          </div>
          <div style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 4 }}>AVG CONFIDENCE</div>
        </div>
      </div>

      {/* Filter pills */}
      {adverseArticles.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {FILTER_LABELS.map(({ key, label }) => {
            const count = key === 'ALL' ? adverseArticles.length : (catCounts[key as AdverseCategory] ?? 0);
            const color = key === 'ALL' ? '#00C8FF' : ADVERSE_COLOR[key as AdverseCategory];
            return (
              <FilterPill
                key={key}
                label={label}
                active={activeFilter === key}
                color={color}
                count={count}
                onClick={() => setActiveFilter(key)}
              />
            );
          })}
        </div>
      )}

      {/* Article cards */}
      {filtered.length > 0 ? (
        <div className="flex flex-col gap-3">
          {filtered.map((article, i) => {
            const col = ADVERSE_COLOR[article.category];
            return (
              <div
                key={i}
                style={{
                  background: '#0C1118',
                  border: '1px solid #182030',
                  borderLeft: `3px solid ${col}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                }}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p style={{ color: '#DCE8F5', fontSize: 12, fontWeight: 600, lineHeight: 1.4, flex: 1 }}>
                    {article.title}
                  </p>
                  <span
                    style={{
                      background: `${col}15`,
                      color: col,
                      border: `1px solid ${col}30`,
                      fontSize: 8,
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      padding: '2px 7px',
                      borderRadius: 99,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {categoryLabel(article.category)}
                  </span>
                </div>

                {article.summary && (
                  <p style={{ color: '#5A7A9A', fontSize: 10, lineHeight: 1.5, marginBottom: 8 }}>
                    {article.summary}
                  </p>
                )}

                {!article.summary && article.snippet && (
                  <p style={{ color: '#5A7A9A', fontSize: 10, lineHeight: 1.5, marginBottom: 8 }}>
                    {article.snippet}
                  </p>
                )}

                {article.fraud_amount && (
                  <p style={{ color: '#F0B429', fontSize: 10, fontFamily: 'monospace', marginBottom: 6 }}>
                    Alleged amount: {article.fraud_amount}
                  </p>
                )}

                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-3">
                    <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
                      {article.source}
                    </span>
                    <span style={{ color: '#283A50', fontSize: 9, fontFamily: 'monospace' }}>
                      {formatDate(article.published_date)}
                    </span>
                  </div>
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1"
                    style={{ color: '#00C8FF', fontSize: 9, fontFamily: 'monospace', textDecoration: 'none' }}
                  >
                    Read more <ExternalLink size={9} />
                  </a>
                </div>

                <ConfidenceBar value={article.confidence} color={col} height={3} showLabel />
              </div>
            );
          })}
        </div>
      ) : adverseArticles.length === 0 ? (
        <div
          style={{ background: '#001A08', border: '1px solid #00C85330', borderRadius: 8, padding: '32px', textAlign: 'center' }}
        >
          <Newspaper size={24} style={{ color: '#283A50', margin: '0 auto 10px' }} />
          <p style={{ color: '#00C853', fontSize: 11, fontFamily: 'monospace' }}>No adverse media detected</p>
        </div>
      ) : (
        <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '24px', textAlign: 'center' }}>
          <p style={{ color: '#283A50', fontSize: 10, fontFamily: 'monospace' }}>
            No articles in this category
          </p>
        </div>
      )}

      {/* Category distribution bar */}
      {adverseArticles.length > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            CATEGORY DISTRIBUTION
          </p>
          <div className="flex flex-col gap-2">
            {Object.entries(catCounts).map(([cat, count]) => {
              const col = ADVERSE_COLOR[cat as AdverseCategory];
              const pct = adverseArticles.length > 0 ? (count / adverseArticles.length) * 100 : 0;
              return (
                <div key={cat} className="flex items-center gap-3">
                  <span
                    style={{ color: col, fontFamily: 'monospace', fontSize: 9, minWidth: 130, flexShrink: 0 }}
                  >
                    {categoryLabel(cat as AdverseCategory)}
                  </span>
                  <div style={{ flex: 1, background: '#0F1928', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${pct}%`,
                        height: 6,
                        background: col,
                        borderRadius: 4,
                        transition: 'width 0.4s ease',
                      }}
                    />
                  </div>
                  <span style={{ color: col, fontFamily: 'monospace', fontSize: 9, fontWeight: 700, minWidth: 20, textAlign: 'right' }}>
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Highest confidence */}
      {adverseArticles.length > 0 && (
        <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '10px 14px' }}>
          <div className="flex items-center justify-between mb-2">
            <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>HIGHEST ARTICLE CONFIDENCE</span>
            <span style={{ color: ADVERSE_COLOR[highestSeverity], fontFamily: 'monospace', fontWeight: 800, fontSize: 12 }}>
              {Math.round(highestConfidence * 100)}%
            </span>
          </div>
          <ConfidenceBar value={highestConfidence} color={ADVERSE_COLOR[highestSeverity]} height={4} />
        </div>
      )}
    </motion.div>
  );
}
